import {
  fetchWidgetConfig,
  findWidgetScript,
  mountWidget,
  resolveApiBase,
  type ProactiveConfig,
  type WidgetConfig,
  type WidgetController,
} from "./mount";
import { ingestEvent, pageContext, type AnalyticsEventType } from "./analyticsEvents";
import {
  buildSessionContext,
  getSessionHistory,
  resolveWebSocketUrl,
  startOrResumeSession,
  type ChatMessage,
} from "./chatApi";

async function boot(): Promise<void> {
  const script = findWidgetScript();
  if (!script) {
    console.warn("[Veroliq] Could not find widget script with data-site-id.");
    return;
  }
  const siteId = script.getAttribute("data-site-id")?.trim();
  if (!siteId) {
    console.warn("[Veroliq] Missing data-site-id on widget script.");
    return;
  }
  const apiBase = resolveApiBase(script);

  try {
    const fullConfig = await fetchWidgetConfig(apiBase, siteId);
    const session = await startOrResumeSession(apiBase, siteId, buildSessionContext(script));

    if (session.sessionId) {
      ingestEvent(apiBase, siteId, session.sessionId, {
        type: "PAGE_VIEW",
        ...pageContext(),
        meta: { navIndex: 0 },
      });
    }

    let history: ChatMessage[] = [];
    try {
      history = await getSessionHistory(apiBase, siteId, session.sessionToken);
    } catch {
      /* history optional */
    }

    const websocketUrl = resolveWebSocketUrl(apiBase, session.sessionToken);
    const widget = mountWidget({
      config: fullConfig.config,
      proactive: fullConfig.proactive,
      siteId,
      apiBase,
      sessionToken: session.sessionToken,
      sessionId: session.sessionId,
      websocketUrl,
      history,
    });

    // Track all SPA page navigations (pushState / replaceState / popstate)
    let navIndex = 0;
    installNavigationWatcher((newUrl, prevUrl) => {
      navIndex++;
      if (session.sessionId) {
        ingestEvent(apiBase, siteId, session.sessionId, {
          type: "PAGE_VIEW",
          url: newUrl,
          referrer: prevUrl,
          meta: { navIndex },
        });
      }
      widget.sendPageChange(newUrl, prevUrl);
    });

    // Schedule proactive callouts after widget is fully mounted
    scheduleCallouts(
      widget,
      history,
      fullConfig.config,
      fullConfig.proactive,
      apiBase,
      siteId,
      session.sessionId,
    );
  } catch (err: unknown) {
    console.error("[Veroliq] Failed to start widget:", err);
  }
}

/** Patches history.pushState / replaceState and listens to popstate to detect SPA navigation. */
function installNavigationWatcher(
  onNavigate: (newUrl: string, prevUrl: string) => void,
): void {
  // Guard against double-installation if the script is loaded more than once
  if ((window as any).__vqNavInstalled) return;
  (window as any).__vqNavInstalled = true;

  let currentUrl = window.location.href;

  function handleNav(): void {
    const newUrl = window.location.href;
    if (newUrl === currentUrl) return;
    const prev = currentUrl;
    currentUrl = newUrl;
    onNavigate(newUrl, prev);
  }

  const origPush = history.pushState.bind(history);
  const origReplace = history.replaceState.bind(history);

  history.pushState = function (...args: Parameters<typeof origPush>) {
    origPush(...args);
    handleNav();
  };
  history.replaceState = function (...args: Parameters<typeof origReplace>) {
    origReplace(...args);
    handleNav();
  };

  window.addEventListener("popstate", handleNav);
}

function scheduleCallouts(
  widget: WidgetController,
  history: ChatMessage[],
  config: WidgetConfig,
  proactive: ProactiveConfig,
  apiBase: string,
  siteId: string,
  sessionId: string | null,
): void {
  const VQ_SESSION_PROMPTED = `vq_prompted_${siteId}`;
  const VQ_LIFETIME_DISMISSED = `vq_dismissed_${siteId}`;

  function track(type: AnalyticsEventType, meta: Record<string, unknown>): void {
    if (sessionId) {
      ingestEvent(apiBase, siteId, sessionId, { type, ...pageContext(), meta });
    }
  }

  // ── Resume callout: returning visitor with prior conversation ──
  if (history.length > 0 && !widget.isOpen()) {
    setTimeout(() => {
      if (widget.isOpen()) return;
      widget.showCallout(
        "resume",
        "Welcome back! Want to pick up where we left off?",
        10000,
        {
          onClick: () => {
            track("PROACTIVE_CLICKED", { calloutType: "resume", promptNumber: 1 });
            track("SESSION_RESUMED", {});
          },
          onDismiss: () =>
            track("PROACTIVE_DISMISSED", { calloutType: "resume", promptNumber: 1 }),
          onExpire: () =>
            track("PROACTIVE_EXPIRED", { calloutType: "resume", promptNumber: 1 }),
        },
      );
      track("PROACTIVE_SHOWN", { calloutType: "resume", promptNumber: 1 });
    }, 1500);
    return; // resume and intro are mutually exclusive
  }

  // ── Intro callout: new visitor proactive greeting ──
  if (!proactive.enabled) return;

  const lifetimeDismissals = (() => {
    try {
      const raw = localStorage.getItem(VQ_LIFETIME_DISMISSED);
      if (!raw) return 0;
      // Legacy format: plain number string
      if (/^\d+$/.test(raw)) return parseInt(raw, 10) || 0;
      const parsed = JSON.parse(raw) as { count?: number; at?: number };
      // Reset after 7 days
      if (Date.now() - (parsed.at ?? 0) > 7 * 86_400_000) return 0;
      return parsed.count ?? 0;
    } catch {
      return 0;
    }
  })();
  if (lifetimeDismissals >= 3) return;

  const sessionPrompts = (() => {
    try {
      return parseInt(sessionStorage.getItem(VQ_SESSION_PROMPTED) ?? "0", 10) || 0;
    } catch {
      return 0;
    }
  })();
  const maxPrompts = proactive.maxPromptsPerSession;
  if (sessionPrompts >= maxPrompts) return;

  if (
    proactive.suppressOnMobile &&
    typeof window !== "undefined" &&
    window.matchMedia?.("(max-width: 640px)").matches
  ) {
    return;
  }

  const resolvedMessage = (
    proactive.message || `Hey! I'm ${config.companyName}. How can I help?`
  ).replace(/\{name\}/g, config.companyName);

  let promptCount = sessionPrompts;

  function doShowIntro(): void {
    if (widget.isOpen() || promptCount >= maxPrompts) return;

    promptCount++;
    try {
      sessionStorage.setItem(VQ_SESSION_PROMPTED, String(promptCount));
    } catch {
      /* ignore */
    }

    track("PROACTIVE_SHOWN", { calloutType: "intro", promptNumber: promptCount });

    const isLastPrompt = promptCount >= maxPrompts;

    widget.showCallout("intro", resolvedMessage, proactive.autoDismissMs, {
      onClick: () =>
        track("PROACTIVE_CLICKED", { calloutType: "intro", promptNumber: promptCount }),
      onDismiss: () => {
        try {
          const raw = localStorage.getItem(VQ_LIFETIME_DISMISSED);
          let count = 0;
          if (raw) {
            if (/^\d+$/.test(raw)) count = parseInt(raw, 10) || 0;
            else count = (JSON.parse(raw) as { count?: number }).count ?? 0;
          }
          localStorage.setItem(VQ_LIFETIME_DISMISSED, JSON.stringify({ count: count + 1, at: Date.now() }));
        } catch {
          /* ignore */
        }
        track("PROACTIVE_DISMISSED", { calloutType: "intro", promptNumber: promptCount });
        if (!isLastPrompt) {
          setTimeout(doShowIntro, proactive.followUpDelayMs);
        }
      },
      onExpire: () => {
        track("PROACTIVE_EXPIRED", { calloutType: "intro", promptNumber: promptCount });
        if (!isLastPrompt) {
          setTimeout(doShowIntro, proactive.followUpDelayMs);
        }
      },
    });
  }

  let introFired = false;
  function fireIntroOnce(): void {
    if (introFired) return;
    introFired = true;
    doShowIntro();
  }

  // Scroll-depth trigger: show callout when visitor has scrolled 50% (signals intent)
  // whichever fires first (scroll or timer) wins; the other is a no-op via introFired flag
  const scrollHandler = () => {
    const pct =
      (window.scrollY + window.innerHeight) /
      Math.max(document.documentElement.scrollHeight, window.innerHeight + 1);
    if (pct >= 0.5) {
      window.removeEventListener("scroll", scrollHandler, { passive: true } as EventListenerOptions);
      fireIntroOnce();
    }
  };
  window.addEventListener("scroll", scrollHandler, { passive: true });

  setTimeout(fireIntroOnce, proactive.initialDelayMs);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    void boot();
  });
} else {
  void boot();
}
